import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { Client, HSTFrequency } from '../types';
import { clientApi, userApi } from '../services/api';
import { Switch, FormControlLabel } from '@mui/material';

const ClientForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  
  // Get current user at the component level
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [existingEmployees, setExistingEmployees] = useState<Array<{name: string, email: string}>>([]);

  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    hstFrequency: HSTFrequency.MONTHLY,
    periodStartDate: '',
    periodEndDate: '',
    dueDate: '',
    employeeAssigned: '',
    employeeEmail: '',
    ccEmails: [] as string[],
    emailNotificationsMuted: false,        
    frequencyChangeDate: '',               
    newFrequency: '' as HSTFrequency | '', 
  });

  const [ccEmailInput, setCcEmailInput] = useState('');
  const [manualDateEdit, setManualDateEdit] = useState({
    periodEnd: false,
    dueDate: false,
  });

  useEffect(() => {
    if (isEditing && id) {
      fetchClient();
    }
    fetchExistingEmployees();
  }, [id, isEditing]);
  const fetchExistingEmployees = async () => {
    try {
      const response = await userApi.getEmployees();
      const users = response.data.data || [];
      
      // Only get users with EMPLOYEE or ADMIN role
      const employees = users
        .filter((u: any) => u.role === 'EMPLOYEE' || u.role === 'ADMIN')
        .map((u: any) => ({ name: u.name, email: u.email }));
      
      // Sort to put current user first (if they're in the list)
      if (currentUser && currentUser.email) {
        employees.sort((a: {name: string, email: string}, b: {name: string, email: string}) => {
          if (a.email === currentUser.email) return -1;
          if (b.email === currentUser.email) return 1;
          return a.name.localeCompare(b.name);
        });
      }
      
      console.log('📋 Loaded employees:', employees);
      setExistingEmployees(employees);
            console.log('✅ Number of employees loaded:', employees.length); 
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchClient = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await clientApi.getById(id);
      const client: Client = response.data.data;
      
    setFormData({
      clientName: client.clientName,
      clientEmail: client.clientEmail,
      hstFrequency: client.hstFrequency,
      periodStartDate: client.periodStartDate.split('T')[0],
      periodEndDate: client.periodEndDate.split('T')[0],
      dueDate: client.dueDate.split('T')[0],
      employeeAssigned: client.employeeAssigned,
      employeeEmail: client.employeeEmail,
      ccEmails: client.ccEmails || [],
      emailNotificationsMuted: client.emailNotificationsMuted || false,  
      frequencyChangeDate: client.frequencyChangeDate ? client.frequencyChangeDate.split('T')[0] : '',  
      newFrequency: client.newFrequency || '',  
    });
    } catch (error) {
      console.error('Error fetching client:', error);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate period end date based on frequency
  const calculatePeriodEndDate = (startDate: string, frequency: HSTFrequency): string => {
    if (!startDate) return '';

    const start = new Date(startDate + 'T00:00:00');
    let endDate = new Date(start);

    switch (frequency) {
      case HSTFrequency.MONTHLY:
        // Last day of the same month
        endDate = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        break;
      case HSTFrequency.QUARTERLY:
        // Last day of the 3rd month
        endDate = new Date(start.getFullYear(), start.getMonth() + 3, 0);
        break;
      case HSTFrequency.ANNUAL:
        // Last day of the 12th month
        endDate = new Date(start.getFullYear(), start.getMonth() + 12, 0);
        break;
    }

    return endDate.toISOString().split('T')[0];
  };

  // Calculate due date
  const calculateDueDate = (periodEndDate: string, frequency: HSTFrequency): string => {
    if (!periodEndDate) return '';

    const endDate = new Date(periodEndDate + 'T00:00:00');
    let dueDate: Date;

    if (frequency === HSTFrequency.ANNUAL) {
      // Annual: 3 months after period end (last day of 3rd month)
      dueDate = new Date(endDate.getFullYear(), endDate.getMonth() + 4, 0);
    } else {
      // Monthly/Quarterly: 1 month after period end (last day of next month)
      dueDate = new Date(endDate.getFullYear(), endDate.getMonth() + 2, 0);
    }

    return dueDate.toISOString().split('T')[0];
  };

  const handleInputChange = (field: string, value: any) => {
    // Track if user manually edited period end or due date
    if (field === 'periodEndDate') {
      setManualDateEdit(prev => ({ ...prev, periodEnd: true }));
    }
    if (field === 'dueDate') {
      setManualDateEdit(prev => ({ ...prev, dueDate: true }));
    }
    // Reset manual edit flags when frequency changes
      if (field === 'hstFrequency') {
        setManualDateEdit({ periodEnd: false, dueDate: false });
      }
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === 'periodStartDate' || field === 'hstFrequency') {
        const startDate = field === 'periodStartDate' ? value : prev.periodStartDate;
        const frequency = field === 'hstFrequency' ? value : prev.hstFrequency;

        if (startDate) {
          // Only auto-calculate period end if not manually edited
          if (!manualDateEdit.periodEnd) {
            updated.periodEndDate = calculatePeriodEndDate(startDate, frequency);
          }
          
          // Only auto-calculate due date if not manually edited
          if (!manualDateEdit.dueDate) {
            updated.dueDate = calculateDueDate(updated.periodEndDate || prev.periodEndDate, frequency);
          }
        }
      }

      // If user edits period end date, recalculate due date (unless due date was also manually edited)
      if (field === 'periodEndDate' && !manualDateEdit.dueDate) {
        updated.dueDate = calculateDueDate(value, prev.hstFrequency);
      }

      return updated;
    });
  };

  const handleAddCcEmail = () => {
    if (ccEmailInput.trim() && !formData.ccEmails.includes(ccEmailInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        ccEmails: [...prev.ccEmails, ccEmailInput.trim()],
      }));
      setCcEmailInput('');
    }
  };

  const handleRemoveCcEmail = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      ccEmails: prev.ccEmails.filter((e) => e !== email),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setLoading(true);

      // Ensure ccEmails is always an array
      const submitData = {
        ...formData,
        ccEmails: formData.ccEmails || [],
      };

      console.log('Submitting client data:', submitData); // DEBUG: Check what's being sent

      if (isEditing && id) {
        await clientApi.update(id, submitData);
        setSuccess('Client updated successfully!');
      } else {
        await clientApi.create(submitData);
        setSuccess('Client created successfully!');
      }

      setTimeout(() => navigate('/clients'), 1500);
    } catch (error: any) {
      console.error('Error saving client:', error);
      setError(error.response?.data?.message || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/clients')}
        sx={{ 
          mb: 3,
          textTransform: 'none',
          color: '#667eea',
          fontWeight: 600,
          '&:hover': { backgroundColor: '#F3F4F6' }
        }}
      >
        Back to Clients
      </Button>

      <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} elevation={0}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, fontSize: '2rem' }}>
          {isEditing ? 'Edit Client' : 'Add New Client'}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 4, fontSize: '1rem', color: '#6B7280' }}>
          {isEditing ? 'Update client information' : 'Enter client details for HST/GST filing tracking'}
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Client Information */}
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, fontSize: '1.25rem' }}>
              Client Information
            </Typography>

            <TextField
              label="Client Name"
              required
              fullWidth
              value={formData.clientName}
              onChange={(e) => handleInputChange('clientName', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            />

            <TextField
              label="Client Email"
              type="email"
              required
              fullWidth
              value={formData.clientEmail}
              onChange={(e) => handleInputChange('clientEmail', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            />

            {/* Filing Details */}
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, fontSize: '1.25rem' }}>
              Filing Details
            </Typography>

            <TextField
              select
              label="Filing Frequency"
              required
              fullWidth
              value={formData.hstFrequency}
              onChange={(e) => handleInputChange('hstFrequency', e.target.value as HSTFrequency)}
              disabled={isEditing}
              helperText={isEditing ? "Filing frequency cannot be changed after creation" : ""}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            >
              <MenuItem value={HSTFrequency.MONTHLY}>Monthly</MenuItem>
              <MenuItem value={HSTFrequency.QUARTERLY}>Quarterly</MenuItem>
              <MenuItem value={HSTFrequency.ANNUAL}>Annual</MenuItem>
            </TextField>

            <TextField
              label="Period Start Date"
              type="date"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.periodStartDate}
              onChange={(e) => handleInputChange('periodStartDate', e.target.value)}
              disabled={isEditing}
              helperText={isEditing ? "Period dates cannot be changed after creation" : ""}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            />

            <TextField
              label="Period End Date"
              type="date"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.periodEndDate}
              onChange={(e) => handleInputChange('periodEndDate', e.target.value)}
              disabled={isEditing}
              helperText={isEditing ? "Period dates cannot be changed after creation" : "Auto-calculated (editable)"}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            />

            <TextField
              label="Filing Due Date"
              type="date"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.dueDate}
              onChange={(e) => handleInputChange('dueDate', e.target.value)}
              disabled={isEditing}
              helperText={isEditing ? "Due date cannot be changed after creation" : "Auto-calculated (editable)"}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            />

            {/* Employee Assignment */}
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, fontSize: '1.25rem' }}>
              Employee Assignment
            </Typography>

            <Autocomplete
              freeSolo
              openOnFocus
              options={existingEmployees}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
              inputValue={formData.employeeAssigned}
              value={existingEmployees.find(emp => emp.name === formData.employeeAssigned) || null}
              onChange={(event, newValue) => {
                if (typeof newValue === 'object' && newValue !== null) {
                  // User selected from dropdown - auto-populate email
                  handleInputChange('employeeAssigned', newValue.name);
                  handleInputChange('employeeEmail', newValue.email);
                } else if (typeof newValue === 'string') {
                  // User typed manually
                  handleInputChange('employeeAssigned', newValue);
                } else {
                  // Cleared
                  handleInputChange('employeeAssigned', '');
                }
              }}
              onInputChange={(event, newInputValue) => {
                if (event) {
                  handleInputChange('employeeAssigned', newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee Assigned"
                  required
                  helperText="Select existing employee or type new name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                    },
                  }}
                />
              )}
            />

            <TextField
              label="Employee Email"
              type="email"
              required
              fullWidth
              value={formData.employeeEmail}
              onChange={(e) => handleInputChange('employeeEmail', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                },
              }}
            />

            {/* CC Emails */}
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, fontSize: '1.25rem' }}>
              CC Recipients (Optional)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="CC Email"
                type="email"
                fullWidth
                value={ccEmailInput}
                onChange={(e) => setCcEmailInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCcEmail())}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              />
              <Button 
                variant="outlined" 
                onClick={handleAddCcEmail}
                sx={{
                  textTransform: 'none',
                  borderColor: '#667eea',
                  color: '#667eea',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#5568d3',
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                Add
              </Button>
            </Box>

            {formData.ccEmails.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.ccEmails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveCcEmail(email)}
                    sx={{
                      backgroundColor: '#F3F4F6',
                      '& .MuiChip-deleteIcon': {
                        color: '#6B7280',
                        '&:hover': { color: '#374151' }
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Submit Button */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                disabled={loading}
                fullWidth
                sx={{
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
                  '&:disabled': { background: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                {loading ? 'Saving...' : isEditing ? 'Update Client' : 'Create Client'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/clients')}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  borderColor: '#E5E7EB',
                  color: '#6B7280',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ClientForm;